import { z } from 'zod';
import { comboEnVentaInput, type ComboEnDocumentoDTO } from './combos.ts';
import { decimalNoNegativo, decimalPositivo, paginacion, uuid } from './comun.ts';

export const lineaCotizacionInput = z.object({
  producto_id: uuid,
  presentacion_id: uuid,
  /** Cantidad de presentaciones. */
  cantidad: decimalPositivo,
  descuento_pct: decimalNoNegativo.refine((v) => Number(v) <= 100, 'El descuento no puede superar 100 %').optional(),
  /** Combo o receta al que pertenece el renglón (spec 20): el grupo de `combos`. */
  combo_grupo: uuid.optional(),
});
export type LineaCotizacionInput = z.infer<typeof lineaCotizacionInput>;

export const cotizacionInput = z.object({
  cliente_id: uuid,
  lineas: z.array(lineaCotizacionInput).min(1, 'Agrega al menos un producto').max(200),
  /** Combos y recetas cotizados (spec 20): el servidor los recalcula y solo con renglones iguales aplica su descuento. */
  combos: z.array(comboEnVentaInput).max(20).optional(),
  /**
   * Descuento global %. Crear con descuento manual exige `ventas.descuento` (403 `descuento_sin_permiso`). Al editar
   * sin ese permiso, el descuento que no se manda se conserva y el que se manda no puede superar al guardado (lo mismo
   * rige para `descuento_pct` de cada renglón). Los descuentos programados por la dueña se aplican solos.
   */
  descuento_pct: decimalNoNegativo.refine((v) => Number(v) <= 100, 'El descuento no puede superar 100 %').optional(),
  /** Días de vigencia; 0 = sin vencimiento. Por defecto config cotizacion_vigencia_dias (0 desde 2026-10-09). */
  vigencia_dias: z.number().int().min(0).max(365).optional(),
  observaciones: z.string().trim().max(500).optional(),
});
export type CotizacionInput = z.infer<typeof cotizacionInput>;

export const calcularCotizacionInput = cotizacionInput.pick({ cliente_id: true, lineas: true, combos: true, descuento_pct: true });
export type CalcularCotizacionInput = z.infer<typeof calcularCotizacionInput>;

export const anularCotizacionInput = z.object({ motivo: z.string().trim().min(3, 'Indica el motivo').max(300) });

export const ESTADOS_COTIZACION = ['vigente', 'vencida', 'facturada', 'anulada'] as const;
export type EstadoCotizacion = (typeof ESTADOS_COTIZACION)[number];

export const cotizacionesFiltros = paginacion.extend({
  q: z.string().trim().max(80).optional(),
  estado: z.enum(ESTADOS_COTIZACION).optional(),
  cliente_id: uuid.optional(),
  desde: z.iso.date().optional(),
  hasta: z.iso.date().optional(),
});
export type CotizacionesFiltros = z.infer<typeof cotizacionesFiltros>;

/** Triple precio por línea: $ divisa (lista) → $ BCV (× factor) → Bs (× tasa BCV). */
export interface LineaCotizacionDTO {
  id: string;
  orden: number;
  producto_id: string;
  presentacion_id: string;
  producto_codigo: string;
  producto_nombre: string;
  producto_unidad: string;
  presentacion_nombre: string;
  unidades_presentacion: string;
  cantidad: string;
  unidades: string;
  precio_unitario_divisa: string;
  precio_unitario: string;
  precio_unitario_bs: string;
  descuento_pct: string;
  subtotal: string;
  subtotal_bs: string;
  /** false si el producto o la presentación están inactivos HOY (caso borde: quitar o sustituir al importar). */
  producto_activo: boolean;
  /** Combo o receta del renglón y su descuento de combo (spec 20); null si no está en un combo. */
  combo_grupo: string | null;
  descuento_combo: string | null;
}

/** Totales en $ reales y en Bs (Bs = $ × tasa 2 por línea; IVA = 16 % de la base en Bs). */
export interface TotalesCotizacionDTO {
  bruto: string;
  descuento: string;
  base: string;
  base_bs: string;
  iva_pct: string;
  iva: string;
  iva_bs: string;
  total: string;
  total_bs: string;
  tasa_bcv: string;
  tasa_paralela: string;
  factor_divisa: string;
}

export interface CalculoCotizacionDTO {
  tasas: { tasa_bcv: string; tasa_paralela: string; factor_divisa: string };
  lineas: (Omit<LineaCotizacionDTO, 'id'> & { estado: 'ok' | 'inactivo' | 'sin_precio'; disponible: string })[];
  combos: ComboEnDocumentoDTO[];
  totales: TotalesCotizacionDTO;
  cliente: { id: string; razon_social: string; rif: string; lista_codigo: string };
}

export interface CotizacionResumenDTO {
  id: string;
  numero_formateado: string;
  version: number;
  cliente_razon_social: string;
  cliente_rif: string;
  total: string;
  total_bs: string;
  tasa_bcv: string;
  /** Tasa 2 congelada: con ella se calcularon los Bs de la cotización. */
  tasa_paralela: string;
  /** null = sin vencimiento. */
  vence_en: string | null;
  estado: EstadoCotizacion;
  documento_numero: string | null;
  creado_por_nombre: string;
  creado_en: string;
}

export interface CotizacionDTO {
  id: string;
  numero_formateado: string;
  sucursal_nombre: string;
  sucursal_formato_impresion: string;
  cliente_id: string;
  cliente_razon_social: string;
  cliente_rif: string;
  cliente_direccion_fiscal: string;
  lista_codigo: string;
  version: number;
  tasa_bcv: string;
  tasa_paralela: string;
  factor_divisa: string;
  iva_pct: string;
  descuento_pct: string;
  bruto: string;
  descuento: string;
  base: string;
  base_bs: string;
  iva: string;
  iva_bs: string;
  total: string;
  total_bs: string;
  /** 0 = sin vencimiento (vence_en null). */
  vigencia_dias: number;
  vence_en: string | null;
  estado: EstadoCotizacion;
  documento_id: string | null;
  documento_numero: string | null;
  facturada_en: string | null;
  motivo_anulacion: string | null;
  observaciones: string | null;
  impresiones: number;
  creado_por_nombre: string;
  creado_en: string;
  actualizado_en: string;
  /** true si la tasa 2 o la BCV vigentes difieren de las congeladas → banner "re-tasar" (regla 3). */
  tasa_movida: boolean;
  tasa_bcv_hoy: string;
  tasa_paralela_hoy: string;
  /** Editable / re-tasable mientras no esté facturada ni anulada. */
  editable: boolean;
  lineas: LineaCotizacionDTO[];
  /** Combos y recetas cotizados (foto del combo, spec 20). */
  combos: ComboEnDocumentoDTO[];
}

export interface VersionCotizacionDTO {
  id: string;
  version: number;
  motivo: 'creacion' | 'edicion' | 'retasa';
  tasa_bcv: string;
  tasa_paralela: string;
  factor_divisa: string;
  base: string;
  iva: string;
  total: string;
  total_bs: string;
  usuario_nombre: string;
  creado_en: string;
  lineas: { producto_codigo: string; producto_nombre: string; presentacion_nombre: string; cantidad: string; precio_unitario: string; precio_unitario_bs: string; subtotal: string }[];
}

export interface RetasaResultadoDTO {
  /** true = tasas y precios idénticos: no se creó versión (caso borde). */
  sin_cambios: boolean;
  cotizacion: CotizacionDTO;
}

/** Vista previa de "pasar al POS": líneas al precio y tasa de HOY con avisos por línea (regla 7). */
export interface ImportacionCotizacionDTO {
  cotizacion_id: string;
  numero_formateado: string;
  cliente_id: string;
  cliente_razon_social: string;
  /** Descuento global % de la cotización: el POS lo carga en el carrito (al emitir rige `ventas.descuento` o una autorización). */
  descuento_pct: string;
  lineas: {
    producto_id: string;
    presentacion_id: string;
    producto_codigo: string;
    producto_nombre: string;
    presentacion_nombre: string;
    cantidad: string;
    unidades: string;
    /** Descuento % del renglón que rige HOY (el mayor entre el cotizado y el programado vigente): va al carrito del POS. */
    descuento_pct: string;
    /** true = el descuento del renglón es manual (supera al programado vigente): facturarlo pide permiso o autorización. */
    descuento_manual: boolean;
    /** Combo o receta del renglón (spec 20): en el POS vuelve a ser un bloque y la venta lo valida con su receta vigente. */
    combo_grupo: string | null;
    /** Un servicio no lleva existencia: nunca sale como `disponible_insuficiente`. */
    estado: 'ok' | 'inactivo' | 'sin_precio' | 'disponible_insuficiente';
    disponible: string;
    precio_anterior: string;
    precio_nuevo: string;
    subtotal_anterior: string;
    subtotal_nuevo: string;
  }[];
  tasa_anterior: string;
  tasa_nueva: string;
  total_anterior: string;
  total_nuevo: string;
  total_bs_anterior: string;
  total_bs_nuevo: string;
  /** Mensajes legibles de lo que cambió (tasa, precios, líneas no importables). */
  diferencias: string[];
  /** Combos y recetas de la cotización (spec 20). */
  combos: ComboEnDocumentoDTO[];
}
